import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import type {
	AiSuggestionsEmptyStateType,
	AiSuggestionsRightRailEntryPoint,
} from '@atlaskit/editor-common/analytics';
import { AiSuggestionsRightRailEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import { AiSuggestionsRegenerationTriggerValue } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useEmptyStateExposedAnalytics, useSuggestionsPanelOpenAnalytics } from '../analytics.ts';
import { getVisibleSuggestions } from '../suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from '../useSuggestionsPanelFilters.ts';

import {
	SuggestionsEmptyState,
	type SuggestionsEmptyStateVariant,
} from './SuggestionsEmptyState.tsx';
import { SuggestionsInlineLoadingBar } from './SuggestionsInlineLoadingBar.tsx';
import { SuggestionsLoadingState } from './SuggestionsLoadingState.tsx';
import { SuggestionItem } from './SuggestionItem.tsx';
import { SuggestionsStack } from './SuggestionsStack.tsx';
type AISuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

const getOrderedSuggestions = (state: { aiSuggestionsState?: AISuggestionsSharedState }) => {
	const aiSuggestionsState = state?.aiSuggestionsState;
	if (!aiSuggestionsState) {
		return undefined;
	}
	const result: NonNullable<ReturnType<(typeof aiSuggestionsState)['suggestionById']['get']>>[] =
		[];
	for (const id of aiSuggestionsState.orderedSuggestionIds) {
		const suggestion = aiSuggestionsState.suggestionById.get(id);
		if (suggestion !== undefined) {
			result.push(suggestion);
		}
	}
	return result;
};

const styles = cssMap({
	panelContainer: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		paddingBlock: token('space.100'),
		paddingInline: token('space.250'),
		gap: token('space.200'),
		minHeight: '100%',
	},
});

const emptyStateAnalyticsTypes: Record<SuggestionsEmptyStateVariant, AiSuggestionsEmptyStateType> =
	{
		initial: 'initial',
		noSuggestionsResult: 'noSuggestionsFound',
		allResolved: 'suggestionsResolved',
	};

export const SuggestionsPanel: FC<{
	entryPoint?: AiSuggestionsRightRailEntryPoint;
}> = ({ entryPoint = AiSuggestionsRightRailEntryPointValue.SUGGESTIONS_PANEL }) => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	const {
		suggestions,
		selectedSuggestionId,
		thinkingStatus,
		thinkingStartedAt,
		thinkingProgressTarget,
		regeneratingSuggestionIds,
		thinkingAgentReadyAt,
		reviewingAgentName,
		reviewingAgent,
	} = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		(state: { aiSuggestionsState?: AISuggestionsSharedState }) => {
			return {
				suggestions: getOrderedSuggestions(state),
				selectedSuggestionId: state?.aiSuggestionsState?.selectedSuggestion,
				thinkingStatus: state?.aiSuggestionsState?.thinkingStatus,
				// Shared thinking-session anchor so the inline bar stays in sync with
				// the editor's thinking bar across mount/unmount.
				thinkingStartedAt: state?.aiSuggestionsState?.thinkingStartedAt ?? null,
				thinkingProgressTarget: state?.aiSuggestionsState?.thinkingProgressTarget ?? null,
				// When the reviewing agent became available in this session; anchors the loading
				// copy's rotation (see `loadingRotationStartedAt`).
				thinkingAgentReadyAt: state?.aiSuggestionsState?.thinkingAgentReadyAt ?? null,
				// Returned as the Set itself rather than a derived array — the hook
				// compares selector results shallowly, and this identity only changes
				// when the plugin swaps the set.
				regeneratingSuggestionIds: state?.aiSuggestionsState?.regeneratingSuggestionIds,
				// Names the agent in the loading copy. `undefined` until the plugin resolves it
				// (or when no agent handles the review), which the loading states treat as
				// "fall back to the generic Rovo copy".
				reviewingAgentName: state?.aiSuggestionsState?.reviewingAgent?.name || undefined,
				// The full agent, for its avatar in the inline loading bar. Returned as the
				// plugin's object so the shallow comparison only changes when the agent does.
				reviewingAgent: state?.aiSuggestionsState?.reviewingAgent ?? undefined,
			};
		},
	);

	const isAgentLoading =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		thinkingStatus === 'thinking' && !reviewingAgent && fg('content_reviewer_agent');
	const loadingRotationStartedAt = thinkingAgentReadyAt ?? thinkingStartedAt;
	/**
	 * Bring the list up to date whenever the panel opens — this component is only
	 * rendered while the Suggestions panel is active, so a mount *is* the panel being
	 * opened (and covers the sidebar being opened straight onto this panel, which a
	 * panel-change listener would miss).
	 *
	 * Suggestions already in state get their staleness re-checked after however
	 * much editing happened while the panel was closed. An empty panel remains idle
	 * until the user explicitly starts Review from its empty state.
	 */
	useEffect(() => {
		// Keyed on the api rather than mount-only because it can be absent on the
		// first render if the editor isn't ready yet. It's a stable reference for the
		// life of an editor instance, so this runs once per open.
		const aiSuggestions = fullPageEditorPresetAPI?.aiSuggestions;
		if (!aiSuggestions) {
			return;
		}

		// Read through the api rather than the debounced hook state, which can still
		// hold a value from before the panel opened.
		const state = aiSuggestions.sharedState.currentState();

		// A generation is already running (e.g. started from the sidebar control
		// that opened this panel) — it refreshes the list when it lands.
		if (state?.thinkingStatus === 'thinking') {
			return;
		}

		if ((state?.orderedSuggestionIds.length ?? 0) === 0) {
			return;
		}

		// Regenerate-only: a full refresh would re-fetch, which reorders the list and
		// can resurrect suggestions whose accept/discard status update is in flight.
		void aiSuggestions.actions.refreshSuggestions({
			regenerateOnly: true,
			regenerationTrigger: AiSuggestionsRegenerationTriggerValue.PANEL_OPEN,
		});
	}, [fullPageEditorPresetAPI]);

	const [{ currentFilter, currentSort }] = useSuggestionsPanelFilters();
	const { userId } = useSessionData();

	// The header's filter menu drives what the list shows. Analytics and the regeneration latch
	// below deliberately keep reading the unfiltered list — they describe the document, not the
	// user's current view of it.
	const visibleSuggestions = useMemo(
		() =>
			getVisibleSuggestions(suggestions, {
				currentFilter,
				currentSort,
				currentUserId: userId,
			}),
		[suggestions, currentFilter, currentSort, userId],
	);

	useEffect(() => {
		if (
			selectedSuggestionId &&
			visibleSuggestions &&
			!visibleSuggestions.some((suggestion) => suggestion.id === selectedSuggestionId)
		) {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.selectSuggestion(null, {
				interactionPoint: 'sidebar',
			});
		}
	}, [fullPageEditorPresetAPI, selectedSuggestionId, visibleSuggestions]);

	const hasSuggestions = (visibleSuggestions?.length ?? 0) > 0;
	const hasPendingSuggestions = (suggestions?.length ?? 0) > 0;
	const hasRenderedLoadingStateRef = useRef(false);
	const hadPendingSuggestionsRef = useRef(hasPendingSuggestions);
	const [emptyVariant, setEmptyVariant] = useState<SuggestionsEmptyStateVariant>('initial');
	const isEmptyStateTransitionPending =
		!hasPendingSuggestions &&
		((thinkingStatus === 'complete' && hasRenderedLoadingStateRef.current) ||
			hadPendingSuggestionsRef.current);

	// Latch for the inline loading bar. `thinkingStatus` can flip
	// 'thinking' -> 'complete' in one React commit batch, so we latch on when
	// thinking starts and release once the regenerated suggestions arrive.
	const [isRegenerating, setIsRegenerating] = useState(false);
	const suggestionIdsKey = (suggestions ?? []).map((s: Suggestion) => s?.id).join(',');
	const suggestionIdsKeyAtRegenStartRef = useRef<string | null>(null);

	useEffect(() => {
		const clearedSuggestions = hadPendingSuggestionsRef.current && !hasPendingSuggestions;
		hadPendingSuggestionsRef.current = hasPendingSuggestions;

		const completedReview = thinkingStatus === 'complete' && hasRenderedLoadingStateRef.current;
		hasRenderedLoadingStateRef.current = thinkingStatus === 'thinking';

		if (hasPendingSuggestions) {
			setEmptyVariant('initial');
		} else if (completedReview) {
			setEmptyVariant('noSuggestionsResult');
		} else if (clearedSuggestions) {
			setEmptyVariant('allResolved');
		}
	}, [hasPendingSuggestions, thinkingStatus]);

	// Manage the inline loading-bar latch (see comment above).
	useEffect(() => {
		if (thinkingStatus === 'thinking') {
			// Remember the current set so we can detect when new results arrive.
			suggestionIdsKeyAtRegenStartRef.current = suggestionIdsKey;
			setIsRegenerating(true);
			return;
		}

		if (thinkingStatus === 'idle') {
			// Cancelled / reset.
			suggestionIdsKeyAtRegenStartRef.current = null;
			setIsRegenerating(false);
			return;
		}

		// 'complete': keep the bar until new suggestions arrive (id set changes),
		// with a safety timeout so it can't get stuck if the set is unchanged/empty.
		if (!isRegenerating) {
			return;
		}

		if (suggestionIdsKey !== suggestionIdsKeyAtRegenStartRef.current) {
			suggestionIdsKeyAtRegenStartRef.current = null;
			setIsRegenerating(false);
			return;
		}

		const timeoutId = setTimeout(() => {
			suggestionIdsKeyAtRegenStartRef.current = null;
			setIsRegenerating(false);
		}, 500);
		return () => clearTimeout(timeoutId);
	}, [thinkingStatus, suggestionIdsKey, isRegenerating]);

	const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => () => clearTimeout(navigationTimeoutRef.current), []);

	const handleSelect = useCallback(
		(id: string) => {
			const isDeselecting = selectedSuggestionId === id;
			fullPageEditorPresetAPI?.aiSuggestions?.actions.selectSuggestion(isDeselecting ? null : id, {
				interactionPoint: 'sidebar',
			});
		},
		[fullPageEditorPresetAPI, selectedSuggestionId],
	);

	const selectNextSuggestion = useCallback(
		(id: string) => {
			clearTimeout(navigationTimeoutRef.current);
			if (!visibleSuggestions || visibleSuggestions.length < 2) {
				return;
			}
			const index = visibleSuggestions.findIndex((suggestion) => suggestion.id === id);
			if (index === -1) {
				return;
			}
			// Use the rail's order before removal, including its current filter and sort.
			const nextSuggestion = visibleSuggestions[(index + 1) % visibleSuggestions.length];
			const selectNext = () => {
				fullPageEditorPresetAPI?.aiSuggestions?.actions.selectSuggestion(nextSuggestion.id, {
					interactionPoint: 'sidebar',
				});
			};

			if (isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs')) {
				const acceptShimmerDuration =
					fullPageEditorPresetAPI?.aiSuggestions?.sharedState.currentState()
						?.acceptShimmerDuration ?? 0;

				navigationTimeoutRef.current = setTimeout(selectNext, acceptShimmerDuration);
			} else {
				selectNext();
			}
		},
		[fullPageEditorPresetAPI, visibleSuggestions],
	);

	const handleAccept = useCallback(
		(id: string, isDiffHidden: boolean) => {
			if (
				fullPageEditorPresetAPI?.aiSuggestions?.actions.acceptSuggestion(
					id,
					'sidebar',
					isDiffHidden,
				)
			) {
				selectNextSuggestion(id);
			}
		},
		[fullPageEditorPresetAPI, selectNextSuggestion],
	);

	const handleDiscard = useCallback(
		(id: string) => {
			if (fullPageEditorPresetAPI?.aiSuggestions?.actions.discardSuggestion(id)) {
				selectNextSuggestion(id);
			}
		},
		[fullPageEditorPresetAPI, selectNextSuggestion],
	);

	const handleDismiss = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.discardSuggestion(id);
		},
		[fullPageEditorPresetAPI],
	);

	const handleGoodSuggestion = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.submitSuggestionFeedback(id, 'good', {
				interactionPoint: 'sidebar',
			});
		},
		[fullPageEditorPresetAPI],
	);

	const handleBadSuggestion = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.submitSuggestionFeedback(id, 'bad', {
				interactionPoint: 'sidebar',
			});
		},
		[fullPageEditorPresetAPI],
	);

	const handleToggleDeletedDiffs = useCallback(
		(id: string, hideDeletedDiffs: boolean) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.selectSuggestion(id, {
				hideDeletedDiffs,
				scrollIntoView: false,
			});
		},
		[fullPageEditorPresetAPI],
	);

	// Only animate cards already visible when the sidebar opens, not suggestions created later.
	const [isStackAnimating, setIsStackAnimating] = useState(
		() => (visibleSuggestions?.length ?? 0) > 1,
	);
	const handleAnimationComplete = useCallback(() => setIsStackAnimating(false), []);

	const isThinking = thinkingStatus === 'thinking';
	const showInlineLoadingBar = isThinking || isRegenerating;
	const isFullPanelLoading = !hasSuggestions && isThinking;

	useSuggestionsPanelOpenAnalytics({
		entryPoint,
		fireAnalyticsEvent: fullPageEditorPresetAPI?.analytics?.actions.fireAnalyticsEvent,
		isFullPanelLoading,
		suggestions,
	});
	useEmptyStateExposedAnalytics({
		emptyStateType: emptyStateAnalyticsTypes[emptyVariant],
		enabled: !isFullPanelLoading && !hasPendingSuggestions && !isEmptyStateTransitionPending,
		fireAnalyticsEvent: fullPageEditorPresetAPI?.analytics?.actions.fireAnalyticsEvent,
	});

	if (!visibleSuggestions?.length) {
		if (isFullPanelLoading) {
			return (
				<SuggestionsLoadingState
					api={fullPageEditorPresetAPI}
					agentName={reviewingAgentName}
					isAgentLoading={isAgentLoading}
					startedAt={thinkingStartedAt}
					rotationStartedAt={loadingRotationStartedAt}
					progressTarget={thinkingProgressTarget}
				/>
			);
		}

		return (
			<SuggestionsEmptyState
				api={fullPageEditorPresetAPI}
				variant={hasPendingSuggestions ? 'initial' : emptyVariant}
			/>
		);
	}

	const suggestionItems = visibleSuggestions.map((suggestion: Suggestion) => (
		<SuggestionItem
			key={suggestion.id}
			suggestion={suggestion}
			fireAnalyticsEvent={fullPageEditorPresetAPI?.analytics?.actions.fireAnalyticsEvent}
			isStackAnimating={
				isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs')
					? isStackAnimating
					: undefined
			}
			isSelected={selectedSuggestionId === suggestion.id}
			isRegenerating={regeneratingSuggestionIds?.has(suggestion.id) ?? false}
			onSelect={handleSelect}
			onAccept={handleAccept}
			onDiscard={handleDiscard}
			onDismiss={handleDismiss}
			onGoodSuggestion={handleGoodSuggestion}
			onBadSuggestion={handleBadSuggestion}
			onToggleDeletedDiffs={handleToggleDeletedDiffs}
		/>
	));

	// Suggestions exist: keep the list visible; show the inline bar at the bottom
	// while regenerating.
	return (
		<div
			// Clearance for the footer scrim lives on the scroll container in
			// `SuggestionsPanelLayout`, so every panel state gets it.
			css={styles.panelContainer}
			data-testid="suggestions-panel-list-container"
		>
			{isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs') ? (
				<SuggestionsStack
					isAnimating={isStackAnimating}
					onAnimationComplete={handleAnimationComplete}
				>
					{suggestionItems}
				</SuggestionsStack>
			) : (
				suggestionItems
			)}
			{showInlineLoadingBar && (
				<SuggestionsInlineLoadingBar
					api={fullPageEditorPresetAPI}
					startedAt={loadingRotationStartedAt}
					agent={reviewingAgent}
					isAgentLoading={isAgentLoading}
				/>
			)}
		</div>
	);
};
